import * as R from 'remeda';
import { IntlShape } from 'react-intl';
import { z } from 'zod';
import {
  CODEC_FIELD_TYPES,
  CodecFieldType,
  CodecFieldTypes,
} from './constants';

// we do not want to perform requests or store filters in URL with empty values, such as empty strings, undefined, null or empty arrays
export const isEmptyValue = (value: unknown) =>
  value === null ||
  value === '' ||
  value === undefined ||
  (Array.isArray(value) && value.length === 0);

// BE needs at least 2 characters for startsWith operator, it throws an error otherwise
export const STARTS_WITH_OPERATOR_MIN_LENGTH = 2;

export const getIsStartsWithTooShort = (value: unknown, operator: string) =>
  operator === 'startsWith' &&
  R.isString(value) &&
  value.length < STARTS_WITH_OPERATOR_MIN_LENGTH;

/**
 * An MQL condition is a single `{ field: { $operator: value } }` pair. Anything
 * else (no field, several fields, several operators) yields null — payloads
 * coming from the DB are not trusted to have the shape the encoder produces.
 */
export const parseMqlCondition = (condition: Record<string, unknown>) => {
  const [fieldEntry, ...restFields] = Object.entries(condition);
  if (!fieldEntry || restFields.length > 0) return null;

  const [field, operatorObject] = fieldEntry;
  if (!R.isPlainObject(operatorObject)) return null;

  const [operatorEntry, ...restOperators] = Object.entries(operatorObject);
  if (!operatorEntry || restOperators.length > 0) return null;

  const [mqlOperator, value] = operatorEntry;

  return { field, mqlOperator, value };
};

export const getStringArrayValueWithDefault = (value: unknown) => {
  const parsedUnknown = z.array(z.unknown()).safeParse(value);

  const parsedArray = parsedUnknown.success ? parsedUnknown.data : [];

  return parsedArray.flatMap(v => {
    const parsedString = z.string().safeParse(v);
    return parsedString.success ? parsedString.data : [];
  });
};

export const getStringValueWithDefault = (value?: unknown) => {
  if (!isEmptyValue(value) && typeof value === 'string') return value;
  return '';
};

export const boolValueOptions = (intl: IntlShape) => ({
  valueOptions: [
    {
      label: intl.formatMessage({
        id: 'features.datasets.data.filtering.boolean.true',
      }),
      value: 'true',
    },
    {
      label: intl.formatMessage({
        id: 'features.datasets.data.filtering.boolean.false',
      }),
      value: 'false',
    },
  ],
});

const isCodecField = (value: unknown): value is CodecFieldType =>
  CODEC_FIELD_TYPES.some(type => type === value);

/** A helper fn to reduce wider object types into CodecFieldTypes */
export const pickFieldsWithCodecTypes = (
  fieldTypesObject: Record<string, unknown>
): CodecFieldTypes =>
  R.pipe(
    fieldTypesObject,
    R.pickBy(type => isCodecField(type))
  );
